import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface PerformanceIssueEntry {
  personId: string | null
  firstName: string | null
  surname: string | null
  location: string[] | null
  sheriffType: string | null
  awolCount: number
  unnotifiedAbsenceCount: number
  driverCancellationCount: number
  latenessCount: number
  total: number
}

export interface DriverPerformanceIssuesResponse {
  success: boolean
  performanceIssues: PerformanceIssueEntry[]
  error?: string
}

export interface DriverPerformanceIssuesRequest {
  startDate: string
  endDate: string
  location?: string
  sheriffTypes?: readonly string[]
}

export function useDriverPerformanceIssues(request: DriverPerformanceIssuesRequest) {
  return useQuery<DriverPerformanceIssuesResponse>({
    queryKey: ["driver-performance-issues", request],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const functionUrl = `${getFunctionsBaseUrl()}/driver-performance-issues`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `Failed to fetch performance issues: ${response.statusText}`,
        )
      }

      const data: DriverPerformanceIssuesResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch performance issues")
      }

      return data
    },
    enabled: !!request.startDate && !!request.endDate,
    staleTime: 30 * 1000,
  })
}
